import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useToast } from '../../hooks/useToast';
import { enqueueAndSync, hasPending } from '../../lib/portalQueue';
import { Button, FormField, NumberField, ChoiceKeys, TimePicker, MobileScreen, MobileHeader, MobileActionBar, MobileCard, EmptyState } from '../ui';
import { formatTime12, formatDateShort, timeToMin, computePaidHours, usePortalQueue, notifyOutcome, OfflineBanner } from './portalShared';

// ---------------------------------------------------------------------------
// Hours tab — the daily "Crew Hours" entry (sql/057), Crew portal 2 pick
// A: time ENTRY, no live clock. Rendered once per day for the lead. Every
// crew member across the jobs this lead runs today gets a start / cut
// time (start defaults to the crew-wide start, cut from their billing
// window) and breaks (default = the recap's lunch; per-person override).
// Breaks reduce BOTH paid hours (shown here) and billed hours (the API
// stamps the crew-wide break onto the chosen billed job's actual_hours).
// ---------------------------------------------------------------------------

// Sentinel for "lunch was taken between jobs — bill nobody".
const GAP_BREAK = '__between_jobs__';

// Standard payroll start (Jaylen 2026-08-07): staff report to the office
// at 8:00 AM, so paid time starts there — NOT at the first job's arrival.
const PAYROLL_DEFAULT_START = '08:00';

function CrewHoursPanel({ ledJobs, allCrew, clients, validDate, crewDayHours, token, onSaved, tabs, strip }) {
    const { show } = useToast();
    const queue = usePortalQueue();
    const [saving, setSaving] = useState(false);
    const hoursQueued = hasPending(`dayhours:${validDate}`, queue);

    const isOOSDay = ledJobs.some(j => j.type === 'LONG_DISTANCE');

    const roster = useMemo(() => {
        const ids = [...new Set(ledJobs.flatMap(j => j.crew_ids || []))];
        return ids.map(id => allCrew.find(c => c.id === id) || { id, name: 'Crew member' }).filter(Boolean);
    }, [ledJobs, allCrew]);

    const existingByCrew = useMemo(() => {
        const m = new Map();
        for (const r of (crewDayHours || [])) m.set(r.crew_member_id, r);
        return m;
    }, [crewDayHours]);

    // Lunch is set on the RECAP (step 1). Each person's break DEFAULTS to
    // it (the longest billable job's actual_lunch_minutes); the per-person
    // fields handle the rare case where one person differs.
    const defaultLunch = useMemo(() => {
        let v = null;
        for (const j of ledJobs) {
            if (j.type === 'WALKTHROUGH' || j.status === 'cancelled') continue;
            const n = Number(j.actual_lunch_minutes);
            if (Number.isFinite(n) && (v === null || n > v)) v = n;
        }
        return v == null ? 30 : v;
    }, [ledJobs]);
    const crewBreaks = useMemo(() => ({ lunch: defaultLunch, dinner: 0, other: 0 }), [defaultLunch]);

    const billingWindowFor = (crewId) => {
        let earliest = null, latest = null;
        for (const j of ledJobs) {
            if (!(j.crew_ids || []).includes(crewId)) continue;
            const s = j.actual_start_time || j.time || null;
            const e = j.actual_end_time || null;
            if (s && (earliest === null || timeToMin(s) < timeToMin(earliest))) earliest = s;
            if (e && (latest === null || timeToMin(e) > timeToMin(latest))) latest = e;
        }
        return { start: earliest, end: latest };
    };

    // ONE crew-wide start time (everyone reports to the office together),
    // defaulting to the 8:00 payroll standard. Changing it cascades to
    // everyone EXCEPT anyone whose start was individually edited. If
    // saved rows exist, the most common saved start becomes the crew
    // start, and only people who differ count as individually edited.
    const savedCommonStart = useMemo(() => {
        const counts = new Map();
        for (const c of roster) {
            const v = existingByCrew.get(c.id)?.clock_in;
            if (v) counts.set(v, (counts.get(v) || 0) + 1);
        }
        let best = null, bestN = 0;
        for (const [v, n] of counts) if (n > bestN) { best = v; bestN = n; }
        return best;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const [dayStart, setDayStart] = useState(savedCommonStart || PAYROLL_DEFAULT_START);

    // Explicit per-person edits ONLY. Seeded from any saved crew_day_hours
    // row — a saved start only counts as an individual edit when it
    // differs from the crew's common start.
    const [rows, setRows] = useState(() => {
        const seedStart = savedCommonStart || PAYROLL_DEFAULT_START;
        const out = {};
        for (const c of roster) {
            const ex = existingByCrew.get(c.id);
            if (!ex) continue;
            const entry = {};
            if (ex.clock_in && ex.clock_in !== seedStart) entry.clock_in = ex.clock_in;
            if (ex.clock_out) entry.clock_out = ex.clock_out;
            const l = Number(ex.lunch_minutes) || 0, d = Number(ex.dinner_minutes) || 0, o = Number(ex.other_minutes) || 0;
            if (l !== defaultLunch || d !== 0 || o !== 0) entry.override = { lunch: l, dinner: d, other: o };
            if (Object.keys(entry).length) out[c.id] = entry;
        }
        return out;
    });
    const setRow = (crewId, patch) => setRows(prev => ({ ...prev, [crewId]: { ...prev[crewId], ...patch } }));

    const effClockIn = (crewId) => {
        const r = rows[crewId] || {};
        return r.clock_in !== undefined ? r.clock_in : (dayStart || '');
    };
    const effClockOut = (crewId) => {
        const r = rows[crewId] || {};
        return r.clock_out !== undefined ? r.clock_out : (billingWindowFor(crewId).end || '');
    };
    const crewBreakTotal = (crewBreaks.lunch || 0) + (crewBreaks.dinner || 0) + (crewBreaks.other || 0);
    const effBreaks = (crewId) => rows[crewId]?.override || crewBreaks;
    const effBreakTotal = (crewId) => {
        const b = effBreaks(crewId);
        return (b.lunch || 0) + (b.dinner || 0) + (b.other || 0);
    };
    const setBreak = (crewId, kind, raw) => {
        const v = Math.max(0, Math.round(Number(raw) || 0));
        setRow(crewId, { override: { ...effBreaks(crewId), [kind]: v } });
    };

    // Billable jobs the lead runs today. The crew-wide break deducts from
    // exactly ONE of them, or none ("between jobs"). Single-job days
    // auto-assign — no UI.
    const billableJobs = useMemo(() => ledJobs.filter(j => j.type !== 'WALKTHROUGH' && j.status !== 'cancelled'), [ledJobs]);
    const jobDuration = (j) => {
        const s = timeToMin(j.actual_start_time || j.time);
        const e = timeToMin(j.actual_end_time);
        return (Number.isNaN(s) || Number.isNaN(e)) ? 0 : Math.max(0, e - s);
    };
    const clientName = (j) => (clients || []).find(c => c.id === j.client_id)?.name || 'Job';
    const jobWindowLabel = (j) => {
        const s = j.actual_start_time || j.time;
        return s ? `${formatTime12(s)}${j.actual_end_time ? `–${formatTime12(j.actual_end_time)}` : ''}` : 'time TBD';
    };
    const [breakJobId, setBreakJobId] = useState(GAP_BREAK);
    // Self-correcting prefill (QA M5 BUG-1): re-derive whenever the job
    // list changes, unless the lead explicitly picked.
    const userPickedBreakRef = useRef(false);
    const pickBreakJob = (id) => { userPickedBreakRef.current = true; setBreakJobId(id); };
    useEffect(() => {
        if (userPickedBreakRef.current) {
            if (breakJobId === GAP_BREAK || billableJobs.some(j => j.id === breakJobId)) return;
            userPickedBreakRef.current = false;
        }
        const withBreak = billableJobs.filter(j => Number(j.actual_lunch_minutes) > 0);
        if (withBreak.length === 1) setBreakJobId(withBreak[0].id);
        else if (billableJobs.length === 0) setBreakJobId(GAP_BREAK);
        else setBreakJobId([...billableJobs].sort((a, b) => jobDuration(b) - jobDuration(a))[0].id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [billableJobs]);

    const save = async () => {
        setSaving(true);
        try {
            const payloadRows = roster.map(c => {
                const r = rows[c.id] || {};
                const b = r.override || crewBreaks;
                return {
                    crew_member_id: c.id,
                    clock_in: effClockIn(c.id) || null,
                    clock_out: effClockOut(c.id) || null,
                    lunch_minutes: b.lunch || 0,
                    dinner_minutes: b.dinner || 0,
                    other_minutes: b.other || 0,
                };
            });
            const { outcome, error } = await enqueueAndSync({
                key: `dayhours:${validDate}`,
                action: 'save_day_hours',
                label: 'Crew hours',
                payload: {
                    action: 'save_day_hours',
                    token: token || undefined,
                    workDate: validDate,
                    rows: payloadRows,
                    // Which job the lunch bills against (null = between jobs).
                    breakJobId: breakJobId === GAP_BREAK ? null : breakJobId,
                    // The billing break is the LEAD's EFFECTIVE break — incl.
                    // their own per-person edit (M5 re-verify round 3).
                    crewBreakMinutes: (() => {
                        const leadId = ledJobs[0]?.leader_id || null;
                        return leadId ? effBreakTotal(leadId) : crewBreakTotal;
                    })(),
                },
            });
            const queued = notifyOutcome(show, outcome, error, 'Crew hours saved');
            if (outcome === 'synced') onSaved?.();
            if (queued) { /* inline amber banner shows via hasPending */ }
        } catch (e) {
            show(e?.message || 'Save failed', 'error');
        } finally {
            setSaving(false);
        }
    };

    const header = <MobileHeader title="Hours" sub={`${formatDateShort(validDate)} · ${roster.length} crew`} />;

    if (roster.length === 0) {
        return (
            <MobileScreen header={header} strip={strip} tabs={tabs}>
                <EmptyState title="No crew hours today" hint="Hours show up here once you're leading a job with a crew." />
            </MobileScreen>
        );
    }

    const breakJobItems = [
        ...billableJobs.map(j => ({ value: j.id, label: `${clientName(j)} · ${jobWindowLabel(j)}` })),
        { value: GAP_BREAK, label: 'Between jobs' },
    ];

    return (
        <MobileScreen
            header={header}
            strip={strip}
            action={(
                <MobileActionBar>
                    <Button size="lg" full loading={saving} onClick={save}>Save crew hours</Button>
                </MobileActionBar>
            )}
            tabs={tabs}
        >
            {hoursQueued && <OfflineBanner what="crew hours" />}

            <MobileCard title="Crew start">
                <TimePicker value={dayStart} onChange={setDayStart} ariaLabel="Crew start time (whole crew)" />
                <p className="text-[11px] text-slate-500 mt-1.5 leading-snug">
                    Paid time starts at the office (8:00 standard). Applies to everyone unless you set a person's start below.
                </p>
            </MobileCard>

            {billableJobs.length > 1 && (
                <MobileCard title="Lunch billed to">
                    <ChoiceKeys items={breakJobItems} value={breakJobId} onChange={pickBreakJob} columns={1} ariaLabel="Lunch billed to" keyClassName="!justify-start" />
                    <p className="text-[11px] text-slate-500 mt-1.5 leading-snug">
                        Which job were you on the clock for during lunch? Lunch never bills to the client — this just sets which job's hours it comes off.
                    </p>
                </MobileCard>
            )}

            {roster.map(c => {
                const r = rows[c.id] || {};
                const paid = computePaidHours(effClockIn(c.id), effClockOut(c.id), effBreakTotal(c.id));
                const hasOverride = !!r.override;
                const ci = effClockIn(c.id), co = effClockOut(c.id);
                const b = effBreaks(c.id);
                return (
                    <MobileCard
                        key={c.id}
                        title={c.name}
                        right={<span className="text-sm font-bold text-slate-900 tabular-nums">{paid === null ? '—' : `${paid}h paid`}</span>}
                    >
                        <div className="grid grid-cols-2 gap-3">
                            <FormField label={<>Start{r.clock_in !== undefined && <span className="ml-1 normal-case text-amber-600">· custom</span>}</>} className="mb-0">
                                <TimePicker value={ci} onChange={(v) => setRow(c.id, { clock_in: v })} ariaLabel={`${c.name} start time`} />
                            </FormField>
                            <FormField label="Cut" className="mb-0">
                                <TimePicker value={co} onChange={(v) => setRow(c.id, { clock_out: v })} ariaLabel={`${c.name} cut time`} />
                            </FormField>
                        </div>
                        {r.clock_in !== undefined && (
                            <Button variant="tertiary" size="sm" className="mt-1"
                                onClick={() => setRows(prev => { const n = { ...(prev[c.id] || {}) }; delete n.clock_in; return { ...prev, [c.id]: n }; })}>
                                ↩ Use crew start
                            </Button>
                        )}
                        <div className={`grid gap-3 mt-3 ${isOOSDay ? 'grid-cols-3' : 'grid-cols-2'}`}>
                            <FormField label="Lunch" className="mb-0">
                                <NumberField value={b.lunch || 0} onChange={v => setBreak(c.id, 'lunch', v)} step={15} min={0} suffix="min" decimals={0} ariaLabel={`${c.name} lunch minutes`} />
                            </FormField>
                            {isOOSDay && (
                                <>
                                    <FormField label="Dinner" className="mb-0">
                                        <NumberField value={b.dinner || 0} onChange={v => setBreak(c.id, 'dinner', v)} step={15} min={0} suffix="min" decimals={0} ariaLabel={`${c.name} dinner minutes`} />
                                    </FormField>
                                    <FormField label="Other" className="mb-0">
                                        <NumberField value={b.other || 0} onChange={v => setBreak(c.id, 'other', v)} step={15} min={0} suffix="min" decimals={0} ariaLabel={`${c.name} other break minutes`} />
                                    </FormField>
                                </>
                            )}
                            {!isOOSDay && (
                                <div className="self-end text-[11px] text-slate-500 leading-snug pb-2.5">
                                    {hasOverride ? 'Custom break' : 'From the recap’s lunch'}
                                </div>
                            )}
                        </div>
                        {hasOverride && (
                            <Button variant="tertiary" size="sm" className="mt-1" onClick={() => setRow(c.id, { override: null })}>Reset to lunch</Button>
                        )}
                    </MobileCard>
                );
            })}
            <p className="px-1 text-[11px] text-slate-400 leading-snug">
                Cut times prefill from billing — adjust whoever cut early or stayed late.
            </p>
        </MobileScreen>
    );
}

export default function HoursTab({ isLead, ...rest }) {
    if (!isLead) {
        return (
            <MobileScreen header={<MobileHeader title="Hours" sub={formatDateShort(rest.validDate)} />} strip={rest.strip} tabs={rest.tabs}>
                <EmptyState title="Lead only" hint="Your crew lead enters everyone's hours for the day." />
            </MobileScreen>
        );
    }
    return <CrewHoursPanel {...rest} />;
}
